// Reconnecting WebSocket (spec §6.4): exponential backoff 250 ms → 10 s with
// jitter, app-level ping every 20 s, and a watchdog for silent connections.
import type { ClientMessage, ServerMessage } from '../../shared/protocol.ts';

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

const BACKOFF_MIN_MS = 250;
const BACKOFF_MAX_MS = 10_000;
const PING_MS = 20_000;
const SILENCE_MS = 45_000; // no message (not even a pong) for this long: assume dead

export class ReconnectingSocket {
  status: SocketStatus = 'connecting';
  onOpen: () => void = () => {};
  onMessage: (msg: ServerMessage) => void = () => {};
  onStatus: (status: SocketStatus) => void = () => {};

  private readonly url: string;
  private readonly latencyMs: number;
  private ws: WebSocket | null = null;
  private attempt = 0;
  private retryTimer = 0;
  private pingTimer = 0;
  private lastMessageAt = 0;
  private stopped = false;

  /** `latencyMs` delays every message both ways (development aid for testing sync). */
  constructor(url: string, latencyMs = 0) {
    this.url = url;
    this.latencyMs = latencyMs;
    window.addEventListener('online', () => {
      if (this.status === 'reconnecting') this.reconnectNow();
    });
  }

  connect(): void {
    if (this.stopped) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.setStatus('open');
      this.startPings();
      this.onOpen();
    };
    ws.onmessage = (e) => {
      this.lastMessageAt = Date.now();
      const msg = JSON.parse(e.data as string) as ServerMessage;
      if (this.latencyMs) setTimeout(() => this.onMessage(msg), this.latencyMs);
      else this.onMessage(msg);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      clearInterval(this.pingTimer);
      if (this.stopped) return;
      this.setStatus('reconnecting');
      const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.attempt++);
      this.retryTimer = window.setTimeout(() => this.connect(), delay * (0.5 + Math.random() / 2));
    };
  }

  /** Sends if connected; returns false otherwise (callers re-send after reconnecting). */
  send(msg: ClientMessage): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    const data = JSON.stringify(msg);
    if (this.latencyMs) setTimeout(() => { if (ws.readyState === WebSocket.OPEN) ws.send(data); }, this.latencyMs);
    else ws.send(data);
    return true;
  }

  /** Stops for good (kicked, or leaving the page). */
  close(): void {
    this.stopped = true;
    clearTimeout(this.retryTimer);
    clearInterval(this.pingTimer);
    this.ws?.close();
    this.ws = null;
    this.setStatus('closed');
  }

  /** Drops the current connection so it reconnects (e.g. to test resume). */
  reconnectNow(): void {
    clearTimeout(this.retryTimer);
    this.attempt = 0;
    if (this.ws) this.ws.close();
    else this.connect();
  }

  private startPings(): void {
    clearInterval(this.pingTimer);
    this.pingTimer = window.setInterval(() => {
      if (Date.now() - this.lastMessageAt > SILENCE_MS) {
        this.ws?.close();
        return;
      }
      this.send({ type: 'ping' });
    }, PING_MS);
  }

  private setStatus(s: SocketStatus): void {
    if (s === this.status) return;
    this.status = s;
    this.onStatus(s);
  }
}
